import { AFFILIATE, PHYSICIAN_TEAM } from 'constants/locationTypes';
import type { Typed } from 'services/api/fields/core';

import { Classification, ClassificationOptions } from './Classification';
import { ClientGroupType, ClientGroupTypeOptions } from './ClientGroupType';
import { Collaboration, CollaborationOptions } from './Collaboration';
import { Group, GroupOptions } from './Group';
import { GroupType, GroupTypeOptions } from './GroupType';

export const IMPORT_TYPES = [
  { label: 'Manual Connect', type: 'imported_patient' },
  { label: 'Auto Connect', type: 'patient' },
];

export const MFA_FACTORS: { [key: string]: { independent: boolean; label: string } } = {
  email: { independent: false, label: 'Email' },
  'phone:sms': { independent: true, label: 'Phone (SMS)' },
  otp: { independent: true, label: 'One Time Password' },
  'webauthn-platform': { independent: false, label: 'Biometrics' },
  'webauthn-roaming': { independent: true, label: 'Hardware Key' },
};

export interface MfaConfigOptions {
  enabled: boolean;
  allowedFactors: string[];
  default: null | string;
}

function getMfaDefaults(): MfaConfigOptions {
  return { enabled: false, allowedFactors: [], default: null };
}

export const filterAllowedFactorsByIndependence = (val: boolean, collection: string[]) =>
  collection
    .filter((factorName) => MFA_FACTORS[factorName]?.independent === val)
    .map((factorName) => ({ id: factorName, name: MFA_FACTORS[factorName].label }));

export class MfaConfig implements MfaConfigOptions {
  enabled: boolean;
  allowedFactors: string[];
  default: null | string;

  constructor(options: MfaConfigOptions = getMfaDefaults()) {
    const opts = { ...getMfaDefaults(), ...options };
    this.enabled = opts.enabled || false;
    this.allowedFactors = opts.allowedFactors;
    this.default = opts.default;
  }

  toFormValues() {
    return {
      ...this,
      default: this.default ? { id: this.default, name: MFA_FACTORS[this.default]?.label } : null,
      allowedIndependentFactors: filterAllowedFactorsByIndependence(true, this.allowedFactors),
      allowedDependentFactors: filterAllowedFactorsByIndependence(false, this.allowedFactors),
    };
  }
}

export type SummarizationPrompt = {
  id: string;
  name: string;
  displayName: string;
};

export enum ClientType {
  ADMIN = 'Admin',
  AFFILIATE = 'Affiliate',
  HEALTH_SYSTEM = 'Health System',
  INPATIENT_POST_ACUTE_CARE = 'Inpatient Post Acute Care',
  OUTPATIENT_POST_ACUTE_CARE = 'Outpatient Post Acute Care',
  PAYOR = 'Payor',
  PHYSICIAN_GROUP = 'Physician Group',
}

export enum ImportType {
  IMPORTED_PATIENT = 'imported_patient',
  PATIENT = 'patient',
}

export type ProductAccess = {
  [key in 'traditionalOlio' | 'olioSummarization' | 'referralSummarization' | 'dischargeCalendar']: {
    enabled: boolean;
  };
};

export interface ClientOptions {
  children?: ClientOptions[];
  unownedCollaborations?: CollaborationOptions[];
  clientGroupTypes?: ClientGroupTypeOptions[];
  episodeClassifications?: ClassificationOptions[];
  enabledProviderTypes?: GroupTypeOptions[];
  providedProviderTypes?: GroupTypeOptions[];
  managedProviderTypes?: GroupTypeOptions[];
  groupTypes?: GroupTypeOptions[];
  leafDescendants?: ClientOptions[];
  mfaConfig?: MfaConfigOptions;
  parent?: ClientOptions | null;
  planTypeClassifications?: ClassificationOptions[];
  managerGroupTypesInNetwork?: GroupTypeOptions[];
  networkGroups?: GroupOptions[];
  healthPlanClassifications?: ClassificationOptions[];
  [key: string]: any;
}

function getDefaults(): ClientOptions {
  return {
    id: '',
    children: [],
    clientType: '' as ClientType,
    clientGroupTypes: [],
    collaboratorIds: [],
    unownedCollaborations: [],
    episodeClassifications: [],
    enabledProviderTypes: [],
    providedProviderTypes: [],
    managedProviderTypes: [],
    groupTypeDisplayNames: [],
    groupTypes: [],
    leaf: true,
    leafDescendants: [],
    mfaConfig: { enabled: false, allowedFactors: [], default: null },
    name: '',
    parent: null,
    parentId: null,
    planTypeClassifications: [],
    ssoEnabled: false,
    fileRetentionDays: null,
    enabledFlags: [],
    managerGroupTypesInNetwork: [],
    networkGroups: [],
    healthPlanClassifications: [],
    importType: ImportType.IMPORTED_PATIENT,
    productAccess: {
      traditionalOlio: { enabled: true },
      olioSummarization: { enabled: false },
      referralSummarization: { enabled: false },
      dischargeCalendar: { enabled: false },
    },
    summarizationPromptIds: [],
    summarizationPrompts: [],
  };
}

// eslint-disable-next-line prefer-const
let Client: new <T extends ClientOptions = ClientOptions>(options: T) => Typed<_Client<T>, T>;

class _Client<T extends ClientOptions = ClientOptions> {
  // Transformed nested models
  children!: Client<NonNullable<T['children']>[number]>[];
  unownedCollaborations!: Collaboration[];
  clientGroupTypes!: ClientGroupType[];
  episodeClassifications!: Classification<NonNullable<T['episodeClassifications']>[number]>[];
  enabledProviderTypes!: GroupType[];
  private _managedProviderTypes: GroupType[] | null = null;
  private _providedProviderTypes: GroupType[] | null = null;
  groupTypes!: GroupType[];
  leafDescendants!: Client<NonNullable<T['leafDescendants']>[number]>[];
  mfaConfig!: MfaConfig;
  parent!: Client<NonNullable<T['parent']>> | null;
  planTypeClassifications!: Classification<NonNullable<T['planTypeClassifications']>[number]>[];
  managerGroupTypesInNetwork!: GroupType[];
  private _networkGroupOptions!: GroupOptions[];
  healthPlanClassifications!: Classification<NonNullable<T['healthPlanClassifications']>[number]>[];

  // Getter dependencies (passthrough but declared for getter access)
  id!: string;
  name!: string;
  clientType!: ClientType;
  leaf!: boolean;
  productAccess!: Partial<ProductAccess>;
  enabledFlags!: string[];

  constructor(options: T) {
    const opts = { ...getDefaults(), ...options };
    Object.assign(this, opts);

    this.children = (opts.children ?? []).map((child) => new Client<NonNullable<T['children']>[number]>(child));
    this.unownedCollaborations = (opts.unownedCollaborations ?? []).map((uc) => new Collaboration(uc));
    this.clientGroupTypes = (opts.clientGroupTypes ?? []).map((cgt) => new ClientGroupType(cgt));
    this.episodeClassifications = (opts.episodeClassifications ?? []).map(
      (ec) => new Classification<NonNullable<T['episodeClassifications']>[number]>(ec)
    );
    this.enabledProviderTypes = (opts.enabledProviderTypes ?? []).map((gt) => new GroupType(gt));
    if ((opts.managedProviderTypes ?? []).length) {
      this._managedProviderTypes = (opts.managedProviderTypes ?? []).map((gt) => new GroupType(gt));
    }
    if ((opts.providedProviderTypes ?? []).length) {
      this._providedProviderTypes = (opts.providedProviderTypes ?? []).map((gt) => new GroupType(gt));
    }
    this.groupTypes = (opts.groupTypes ?? []).map((gt) => new GroupType(gt));
    this.leafDescendants = (opts.leafDescendants ?? []).map(
      (child) => new Client<NonNullable<T['leafDescendants']>[number]>(child)
    );
    this.mfaConfig = new MfaConfig(opts.mfaConfig);
    this.parent = opts.parent ? new Client<NonNullable<T['parent']>>(opts.parent) : null;
    this.planTypeClassifications = (opts.planTypeClassifications ?? []).map(
      (p) => new Classification<NonNullable<T['planTypeClassifications']>[number]>(p)
    );
    this.managerGroupTypesInNetwork = (opts.managerGroupTypesInNetwork ?? []).map((gt) => new GroupType(gt));
    this._networkGroupOptions = opts.networkGroups ?? [];
    this.healthPlanClassifications = (opts.healthPlanClassifications ?? []).map(
      (c) => new Classification<NonNullable<T['healthPlanClassifications']>[number]>(c)
    );
  }

  get networkGroups() {
    return this._networkGroupOptions.map((g) => new Group(g));
  }
  set networkGroups(v: Group[]) {
    this._networkGroupOptions = v as unknown as GroupOptions[];
  }
  get clientManagedGroupTypes() {
    return this.clientGroupTypes.filter((cgt) => cgt.isManaged);
  }
  get clientProvidedGroupTypes() {
    return this.clientGroupTypes.filter((cgt) => cgt.isProvided);
  }
  get isAdmin() {
    return this.clientType === ClientType.ADMIN;
  }
  get isAffiliate() {
    return this.clientType === ClientType.AFFILIATE;
  }
  get isHealthSystem() {
    return this.clientType === ClientType.HEALTH_SYSTEM;
  }
  get isOwningClientType() {
    return [ClientType.HEALTH_SYSTEM, ClientType.PAYOR, ClientType.PHYSICIAN_GROUP].includes(this.clientType);
  }
  get isPayor() {
    return this.clientType === ClientType.PAYOR;
  }
  get isParent() {
    return !this.leaf;
  }
  get isPhysicianGroup() {
    return this.clientType === ClientType.PHYSICIAN_GROUP;
  }
  get leafDescendantIds() {
    return this.leafDescendants.map((leaf) => leaf.id);
  }
  get managedProviderTypes() {
    if (this._managedProviderTypes) return this._managedProviderTypes;
    return this.enabledProviderTypes.filter((groupType) =>
      this.clientManagedGroupTypes.map((cgt) => cgt.groupTypeId).find((id) => id === groupType.id)
    );
  }
  set managedProviderTypes(v: GroupType[]) {
    this._managedProviderTypes = v;
  }
  get providedProviderTypes() {
    if (this._providedProviderTypes) return this._providedProviderTypes;
    return this.enabledProviderTypes.filter((groupType) =>
      this.clientProvidedGroupTypes.map((cgt) => cgt.groupTypeId).find((id) => id === groupType.id)
    );
  }
  set providedProviderTypes(v: GroupType[]) {
    this._providedProviderTypes = v;
  }
  get groupTypeIds() {
    return this.groupTypes.map((groupType) => groupType.id);
  }
  get managerGroupTypesForFilters() {
    return this.managerGroupTypesInNetwork.filter((gt) => ![AFFILIATE, PHYSICIAN_TEAM].includes(gt.apiName));
  }
  get isChildCollaborator() {
    return this.unownedCollaborations.length > 0;
  }
  get referralSummarizationEnabled() {
    return !!this.productAccess.referralSummarization?.enabled;
  }
  get olioSummarizationEnabled() {
    return !!this.productAccess.olioSummarization?.enabled;
  }
  get careCoordinationEnabled() {
    return !!this.productAccess.traditionalOlio?.enabled;
  }
  get dischargeCalendarEnabled() {
    return !!this.productAccess.dischargeCalendar?.enabled;
  }
  hasFlag(flag: string) {
    return this.enabledFlags.includes(flag);
  }

  configForGroupType(groupTypeApiNameOrId: string) {
    return this.clientGroupTypes.find((clientGroupType) =>
      [clientGroupType.groupType?.apiName, clientGroupType.groupTypeId].includes(groupTypeApiNameOrId)
    )?.config;
  }

  managesAndProvidesGroupType(groupTypeApiNameOrId: string): boolean {
    return [this.managedProviderTypes, this.providedProviderTypes].every((groupTypes) =>
      groupTypes.some(
        (groupType) => groupType.id === groupTypeApiNameOrId || groupType.apiName === groupTypeApiNameOrId
      )
    );
  }
}

Client = _Client as typeof Client;

export default Client;
export { Client };
export type Client<T extends ClientOptions = ClientOptions> = _Client<T> & Omit<T, keyof _Client<T>>;
